import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import * as React from 'react';

import { cn } from '../utils';

type ViewportContextValue = {
  viewport: HTMLDivElement | null;
  onViewportChange: (node: HTMLDivElement | null) => void;
};

const ViewportContext = React.createContext<ViewportContextValue | null>(null);

const useViewportContext = (consumerName: string) => {
  const context = React.useContext(ViewportContext);
  if (!context) {
    throw new Error(
      `${consumerName} must be rendered inside ScrollableViewport`,
    );
  }
  return context;
};

export const ScrollableViewport = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [viewport, setViewport] = React.useState<HTMLDivElement | null>(null);

  const contextValue = React.useMemo<ViewportContextValue>(
    () => ({ viewport, onViewportChange: setViewport }),
    [viewport],
  );

  return (
    <ViewportContext.Provider value={contextValue}>
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden -m-1 p-1">
        <ScrollUpButton />
        <Viewport>{children}</Viewport>
        <ScrollDownButton />
      </div>
    </ViewportContext.Provider>
  );
};

const Viewport = ({ children }: { children: React.ReactNode }) => {
  const { onViewportChange } = useViewportContext('Viewport');
  return (
    <>
      {/* Hide native scrollbar cross-browser; chevron buttons + keyboard nav drive scrolling. */}
      <style
        dangerouslySetInnerHTML={{
          __html:
            '[data-dropdown-menu-viewport]{scrollbar-width:none;-ms-overflow-style:none;-webkit-overflow-scrolling:touch;}[data-dropdown-menu-viewport]::-webkit-scrollbar{display:none}',
        }}
      />
      <div
        ref={onViewportChange}
        data-dropdown-menu-viewport=""
        className="overflow-y-auto flex-1 min-h-0 -m-1 p-1"
      >
        {children}
      </div>
    </>
  );
};

const ScrollUpButton = () => {
  const { viewport } = useViewportContext('ScrollUpButton');
  const [canScrollUp, setCanScrollUp] = React.useState(false);

  React.useLayoutEffect(() => {
    if (!viewport) return;
    const handleScroll = () => {
      setCanScrollUp(viewport.scrollTop > 0);
    };
    handleScroll();
    viewport.addEventListener('scroll', handleScroll);
    const resizeObserver = new ResizeObserver(handleScroll);
    resizeObserver.observe(viewport);
    return () => {
      viewport.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, [viewport]);

  if (!canScrollUp) return null;

  return (
    <ScrollButtonImpl
      className="top-0"
      onAutoScroll={() => {
        if (viewport) viewport.scrollTop -= getItemHeight(viewport);
      }}
    >
      <ChevronUpIcon className="size-4" />
    </ScrollButtonImpl>
  );
};

const ScrollDownButton = () => {
  const { viewport } = useViewportContext('ScrollDownButton');
  const [canScrollDown, setCanScrollDown] = React.useState(false);

  React.useLayoutEffect(() => {
    if (!viewport) return;
    const handleScroll = () => {
      const maxScroll = viewport.scrollHeight - viewport.clientHeight;
      setCanScrollDown(Math.ceil(viewport.scrollTop) < maxScroll);
    };
    handleScroll();
    viewport.addEventListener('scroll', handleScroll);
    const resizeObserver = new ResizeObserver(handleScroll);
    resizeObserver.observe(viewport);
    return () => {
      viewport.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, [viewport]);

  if (!canScrollDown) return null;

  return (
    <ScrollButtonImpl
      className="bottom-0"
      onAutoScroll={() => {
        if (viewport) viewport.scrollTop += getItemHeight(viewport);
      }}
    >
      <ChevronDownIcon className="size-4" />
    </ScrollButtonImpl>
  );
};

interface ScrollButtonImplProps {
  className?: string;
  onAutoScroll: () => void;
  children: React.ReactNode;
}

const ScrollButtonImpl = ({
  className,
  onAutoScroll,
  children,
}: ScrollButtonImplProps) => {
  const autoScrollTimerRef = React.useRef<number | null>(null);

  const clearAutoScrollTimer = React.useCallback(() => {
    if (autoScrollTimerRef.current !== null) {
      window.clearInterval(autoScrollTimerRef.current);
      autoScrollTimerRef.current = null;
    }
  }, []);

  React.useEffect(() => clearAutoScrollTimer, [clearAutoScrollTimer]);

  const startAutoScroll = () => {
    if (autoScrollTimerRef.current === null) {
      autoScrollTimerRef.current = window.setInterval(onAutoScroll, 50);
    }
  };

  return (
    <div
      aria-hidden
      className={cn(
        'absolute inset-x-0 z-10 bg-popover',
        'flex cursor-default items-center justify-center py-1',
        className,
      )}
      onPointerDown={startAutoScroll}
      onPointerMove={startAutoScroll}
      onPointerLeave={clearAutoScrollTimer}
    >
      {children}
    </div>
  );
};

const getItemHeight = (viewport: HTMLDivElement) => {
  const highlighted = viewport.querySelector<HTMLElement>('[data-highlighted]');
  const item =
    highlighted ?? viewport.querySelector<HTMLElement>('[role^="menuitem"]');
  return item?.offsetHeight ?? 0;
};
